import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LegalPageLayout } from "@/components/LegalPageLayout";
import { LEGAL_LAST_UPDATED, LEGAL_LAST_UPDATED_AR } from "@/lib/legal";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — Lumvio" },
      {
        name: "description",
        content:
          "The terms that govern use of Lumvio, an payment-protected marketplace connecting verified freelancers with clients.",
      },
      { property: "og:title", content: "Terms of Service — Lumvio" },
      { property: "og:description", content: "Terms governing use of the Lumvio verified freelancer workspace." },
      { property: "og:url", content: "https://lumvio.co/terms" },
    ],
    links: [{ rel: "canonical", href: "https://lumvio.co/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  const { i18n } = useTranslation();
  const ar = (i18n.language || "en").startsWith("ar");
  return (
    <LegalPageLayout title={ar ? "شروط الخدمة" : "Terms of Service"} updated={ar ? LEGAL_LAST_UPDATED_AR : LEGAL_LAST_UPDATED}>
      {ar ? <ArabicBody /> : <EnglishBody />}
    </LegalPageLayout>
  );
}

function EnglishBody() {
  return (
    <>
      <p>
        Welcome to Lumvio ("Lumvio", "we", "our", "us"). These Terms of Service ("Terms") govern your access to and use
        of the Lumvio website, applications, and services (collectively, the "Service"), which provide an
        payment-protected marketplace connecting verified freelancers with clients.
      </p>

      <h2>1. Acceptance of Terms</h2>
      <p>
        By creating an account or using the Service, you agree to be bound by these Terms. If you do not agree, do not
        use the Service. You must be at least 18 years old and legally capable of entering into a binding contract.
      </p>

      <h2>2. Accounts and Identity Verification</h2>
      <p>
        Freelancer accounts require successful identity verification through our third-party provider (Stripe Identity).
        You agree to provide accurate, current, and complete information and to keep it up to date. You are responsible
        for all activity that occurs under your account.
      </p>

      <h2>3. Marketplace Role</h2>
      <p>
        Lumvio is a neutral platform. Contracts for services are entered into directly between clients and freelancers.
        Lumvio is not a party to those contracts, is not an employer or agent of any user, and does not guarantee the
        quality, safety, or legality of services offered.
      </p>

      <h2>4. Payment Protection</h2>
      <ul>
        <li>Clients fund milestones into payment protection before work begins.</li>
        <li>Funds are released to freelancers upon client approval of the deliverable.</li>
        <li>Lumvio charges a service fee disclosed at the point of transaction.</li>
        <li>Payouts to freelancer wallets are subject to KYC verification and applicable holds.</li>
      </ul>

      <h2>5. Prohibited Conduct</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Circumvent the payment protection system or pay/receive payments off-platform for platform-sourced work.</li>
        <li>Provide false identity information or impersonate any person.</li>
        <li>Upload unlawful, infringing, harmful, or deceptive content.</li>
        <li>Use the Service to launder funds, evade sanctions, or violate any applicable law.</li>
      </ul>

      <h2>6. Disputes</h2>
      <p>
        Users are encouraged to resolve disputes directly. Where escalation is required, Lumvio may, at its discretion,
        review evidence and make a binding release or refund decision consistent with the Refund Policy.
      </p>

      <h2>7. Intellectual Property</h2>
      <p>
        Freelancers retain ownership of work product until full payment is released from payment protection, at which point
        ownership transfers to the client unless the parties agree otherwise in writing. Lumvio retains all rights to
        the Service itself, including trademarks and platform code.
      </p>

      <h2>8. Termination</h2>
      <p>
        We may suspend or terminate your account for violation of these Terms, suspected fraud, or risk to other users.
        You may close your account at any time; obligations relating to open contracts and protected funds survive
        termination.
      </p>

      <h2>9. Disclaimers</h2>
      <p>
        The Service is provided "as is" without warranties of any kind. To the maximum extent permitted by law, Lumvio
        disclaims all implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, Lumvio's aggregate liability arising out of or relating to the Service
        will not exceed the greater of (a) fees paid by you to Lumvio in the 12 months preceding the claim, or (b) USD
        100.
      </p>

      <h2>11. Changes to the Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will be notified through the Service. Continued
        use after changes take effect constitutes acceptance.
      </p>

      <h2>12. Contact</h2>
      <p>
        Questions about these Terms may be sent to <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>.
      </p>
    </>
  );
}

function ArabicBody() {
  return (
    <>
      <p>
        مرحبًا بك في Lumvio ("Lumvio"، "نحن"، "لنا"، "الخاصة بنا"). تحكم شروط الخدمة هذه ("الشروط") وصولك إلى موقع
        Lumvio وتطبيقاته وخدماته واستخدامها (يُشار إليها مجتمعةً بـ"الخدمة")، والتي توفر سوقًا محميًا بنظام حماية المدفوعات
        محمي المدفوعات يربط المستقلين الموثّقين بالعملاء.
      </p>

      <h2>1. قبول الشروط</h2>
      <p>
        بإنشاء حساب أو استخدام الخدمة، فإنك توافق على الالتزام بهذه الشروط. إذا كنت لا توافق، فلا تستخدم الخدمة. يجب أن
        يكون عمرك 18 عامًا على الأقل وأن تكون مؤهلاً قانونيًا لإبرام عقد ملزم.
      </p>

      <h2>2. الحسابات والتحقق من الهوية</h2>
      <p>
        تتطلب حسابات المستقلين إتمام التحقق من الهوية بنجاح عبر مزودنا الخارجي (Stripe Identity). أنت توافق على تقديم
        معلومات دقيقة وحديثة وكاملة والحفاظ على تحديثها. أنت مسؤول عن جميع الأنشطة التي تجري تحت حسابك.
      </p>

      <h2>3. دور المنصة</h2>
      <p>
        Lumvio منصة محايدة. تُبرم عقود الخدمات مباشرةً بين العملاء والمستقلين. Lumvio ليست طرفًا في تلك العقود، وليست
        صاحب عمل أو وكيلاً لأي مستخدم، ولا تضمن جودة الخدمات المقدَّمة أو سلامتها أو قانونيتها.
      </p>

      <h2>4. حماية المدفوعات</h2>
      <ul>
        <li>يمول العملاء المراحل في نظام حماية المدفوعات قبل بدء العمل.</li>
        <li>تُحوَّل الأموال إلى المستقلين عند موافقة العميل على التسليم.</li>
        <li>تفرض Lumvio رسم خدمة يُفصح عنه عند إتمام المعاملة.</li>
        <li>تخضع التحويلات إلى محافظ المستقلين للتحقق من الهوية (KYC) وللاحتجازات المعمول بها.</li>
      </ul>

      <h2>5. السلوك المحظور</h2>
      <p>أنت توافق على عدم:</p>
      <ul>
        <li>تجاوز نظام حماية المدفوعات أو الدفع/الاستلام خارج المنصة مقابل عمل مصدره المنصة.</li>
        <li>تقديم معلومات هوية مزيفة أو انتحال شخصية أي شخص.</li>
        <li>رفع محتوى غير قانوني أو منتهك للحقوق أو ضار أو مضلل.</li>
        <li>استخدام الخدمة لغسل الأموال أو التهرب من العقوبات أو انتهاك أي قانون معمول به.</li>
      </ul>

      <h2>6. النزاعات</h2>
      <p>
        يُشجَّع المستخدمون على حل النزاعات مباشرةً. عند الحاجة إلى التصعيد، يجوز لـ Lumvio، وفق تقديرها، مراجعة الأدلة
        وإصدار قرار ملزم بالإفراج أو الاسترداد بما يتوافق مع سياسة الاسترداد.
      </p>

      <h2>7. الملكية الفكرية</h2>
      <p>
        يحتفظ المستقلون بملكية نتاج العمل حتى يتم الإفراج عن كامل الدفعة من حماية المدفوعات، وعندها تنتقل الملكية إلى العميل ما
        لم يتفق الطرفان كتابيًا على خلاف ذلك. تحتفظ Lumvio بجميع الحقوق المتعلقة بالخدمة نفسها، بما في ذلك العلامات
        التجارية وكود المنصة.
      </p>

      <h2>8. الإنهاء</h2>
      <p>
        يجوز لنا تعليق أو إنهاء حسابك بسبب انتهاك هذه الشروط، أو الاشتباه في احتيال، أو وجود خطر على المستخدمين
        الآخرين. يمكنك إغلاق حسابك في أي وقت؛ وتبقى الالتزامات المتعلقة بالعقود المفتوحة والأموال المودعة ضمن حماية المدفوعات
        سارية بعد الإنهاء.
      </p>

      <h2>9. إخلاء المسؤولية</h2>
      <p>
        تُقدَّم الخدمة "كما هي" دون أي ضمانات من أي نوع. إلى أقصى حد يسمح به القانون، تُخلي Lumvio مسؤوليتها من جميع
        الضمانات الضمنية المتعلقة بالقابلية للتسويق والملاءمة لغرض معين وعدم الانتهاك.
      </p>

      <h2>10. تحديد المسؤولية</h2>
      <p>
        إلى أقصى حد يسمح به القانون، لن تتجاوز مسؤولية Lumvio الإجمالية الناشئة عن الخدمة أو المتعلقة بها الأكبر من (أ)
        الرسوم المدفوعة من قبلك إلى Lumvio خلال الاثني عشر شهرًا السابقة للمطالبة، أو (ب) 100 دولار أمريكي.
      </p>

      <h2>11. التغييرات على الشروط</h2>
      <p>
        قد نُحدّث هذه الشروط من وقت لآخر. يتم الإخطار بالتغييرات الجوهرية عبر الخدمة. يُعد الاستمرار في الاستخدام بعد
        نفاذ التغييرات قبولاً لها.
      </p>

      <h2>12. التواصل</h2>
      <p>
        يمكن إرسال الأسئلة حول هذه الشروط إلى <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>.
      </p>
    </>
  );
}
